import { documentToReactComponents, type Options } from '@contentful/rich-text-react-renderer'
import { BLOCKS, INLINES } from '@contentful/rich-text-types'
import Image from 'next/image'
import Link from 'next/link'

import { Alert } from '@/components/primitives/Alert/Alert'
import { Heading } from '@/components/primitives/Heading/Heading'
import { asHttpSrc, getImageUrl } from '@/lib/utils'

import DynamicTable from './DynamicTable'

import type { Document } from '@contentful/rich-text-types'

const tableCellOptions: Options = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (_, children) => <p className="mb-1 last:mb-0">{children}</p>,
    [BLOCKS.TABLE_CELL]: (_, children) => children,
    [BLOCKS.TABLE_HEADER_CELL]: (_, children) => children,
  },
}

const options: Options = {
  renderNode: {
    [INLINES.HYPERLINK]: (node, children) => (
      <Link href={node.data.uri} className="underline">
        {children}
      </Link>
    ),
    [INLINES.ASSET_HYPERLINK]: (node, children) => {
      const asset = node.data.target
      if (!asset?.fields?.file?.url) {
        return children
      }

      try {
        const imageUrl = getImageUrl(asset)

        return (
          <a href={imageUrl} target="_blank" rel="noopener noreferrer" className="underline">
            {children}
          </a>
        )
      } catch (error) {
        return <span className="rounded bg-red-100 px-2 py-1 text-sm text-red-700">Error rendering asset link: {String(error)}</span>
      }
    },
    [INLINES.ENTRY_HYPERLINK]: (node, children) => {
      const entry = node.data.target
      const contentType = entry?.sys?.contentType?.sys?.id
      const slug = entry?.fields?.slug
      if (contentType === 'page' && slug) {
        return (
          <Link href={`/${slug}`} className="underline">
            {children}
          </Link>
        )
      }

      if (contentType && slug) {
        return (
          <Link href={`/${contentType}/${slug}`} className="underline">
            {children}
          </Link>
        )
      }

      return children
    },
    [BLOCKS.HEADING_1]: (_, children) => (
      <Heading as="h1" className="font-geist mb-2 normal-case text-grey-900">
        {children}
      </Heading>
    ),
    [BLOCKS.HEADING_2]: (_, children) => (
      <Heading as="h2" className="font-geist mb-2 normal-case text-grey-900">
        {children}
      </Heading>
    ),
    [BLOCKS.HEADING_3]: (_, children) => (
      <Heading as="h3" className="font-geist mb-2 normal-case text-grey-900">
        {children}
      </Heading>
    ),
    [BLOCKS.HEADING_4]: (_, children) => (
      <Heading as="h4" className="font-geist mb-2 normal-case text-grey-900">
        {children}
      </Heading>
    ),
    [BLOCKS.HEADING_5]: (_, children) => (
      <Heading as="h5" className="font-geist mb-2 text-2xl normal-case text-grey-900 xl:text-2xl">
        {children}
      </Heading>
    ),
    [BLOCKS.PARAGRAPH]: (_, children) => {
      const text = Array.isArray(children)
        ? children
            .find((children) => !!children)
            ?.toString()
            .trim()
        : children?.toString().trim()
      if (!text) {
        return <br />
      }

      return <p className="pb-4 last:pb-0">{children}</p>
    },
    [BLOCKS.UL_LIST]: (_, children) => <ul className="mb-4 ml-5 list-disc md:ml-6">{children}</ul>,
    [BLOCKS.OL_LIST]: (_, children) => <ol className="mb-4 ml-5 list-decimal md:ml-6">{children}</ol>,
    [BLOCKS.LIST_ITEM]: (_, children) => <li className="block-list-item">{children}</li>,
    [BLOCKS.QUOTE]: (_, children) => <div className="block_quote border-l-4 border-grey-300 py-2 pl-4">{children}</div>,
    [BLOCKS.HR]: () => <hr className="my-6 border-grey-200" />,
    [BLOCKS.EMBEDDED_ASSET]: (node) => {
      const asset = node.data.target
      if (!asset?.fields?.file?.url) {
        return null
      }

      try {
        // Try using getImageUrl first (it handles the ContentfulAsset type properly)
        let imageUrl
        try {
          imageUrl = getImageUrl(asset)
        } catch {
          // Fallback to direct URL handling
          imageUrl = asHttpSrc(asset?.fields?.file?.url)
        }

        const width = asset?.fields?.file?.details?.image?.width || 800
        const height = asset?.fields?.file?.details?.image?.height || 600
        const alt = asset.fields.title || asset.fields.description || 'Embedded image'

        return (
          imageUrl && (
            <div className="my-6">
              <Image src={imageUrl} alt={alt} width={width} height={height} className="rounded-lg" />
            </div>
          )
        )
      } catch (error) {
        return (
          <div className="my-6 rounded-lg border border-red-200 bg-red-50 p-4">
            <p className="text-sm text-red-600">Error rendering image: {String(error)}</p>
          </div>
        )
      }
    },
    [BLOCKS.TABLE_CELL]: (node) => {
      return (
        <td className="min-w-32 border-r border-grey-200 p-3 text-sm text-grey-700 first:pl-4 last:border-r-0 last:pr-4 md:min-w-40 md:p-4 md:py-[26px] md:first:pl-6 md:last:pr-6">
          {documentToReactComponents(node as Document, tableCellOptions)}
        </td>
      )
    },
    [BLOCKS.TABLE]: (_, children) => <DynamicTable>{children}</DynamicTable>,
    [BLOCKS.EMBEDDED_ENTRY]: ({ data: { target } }) => {
      const contentType = target?.sys?.contentType?.sys?.id
      if (contentType == 'alertToast') {
        const { type, content, title } = target?.fields

        return <Alert description={content} title={title} variant={type?.toLowerCase()} />
      }
    },
    [BLOCKS.TABLE_ROW]: (_, children) => <tr className="border-b border-grey-200 first:bg-primary-500 last:border-b-0">{children}</tr>,
    [BLOCKS.TABLE_HEADER_CELL]: (node) => (
      <th className="border-r border-accent-7 p-3 text-left text-sm font-semibold text-white first:pl-4 last:border-r-0 last:pr-4 md:p-4 md:first:pl-6 md:last:pr-6">
        {documentToReactComponents(node as Document, tableCellOptions)}
      </th>
    ),
  },
}

export const RichTextEditor = ({ content, styleOptions = {} }: { content: Document; styleOptions?: Options }) => {
  return <div className="w-full">{documentToReactComponents(content, { renderNode: { ...options.renderNode, ...styleOptions?.renderNode } })}</div>
}

export default RichTextEditor
